/*
 * HeroDecor — ambient "live engineering system" layer for the hero. The hero
 * <video> itself is never touched. A faint grid drifts (parallax on scroll) and
 * is masked to the right so it never sits under the headline / CTAs. The richer
 * cluster (dependency graph, grid fragments, drifting formula trails, live
 * status badges) is right-aligned and hidden below lg so it never overlaps the
 * copy on tablet / mobile. Everything is pointer-events-none and z-10 (above the
 * video overlays, below the z-20 content).
 */
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Gauge, CheckCircle2 } from "lucide-react";
import { NodeGraph } from "./Decor";

const STATUS_ROWS = [
  ["engine", "online", "green"],
  ["parity", "100%", "green"],
  ["access", "RBAC + MFA", "cyan"],
  ["encryption", "AES-256", "cyan"],
  ["pipeline", "validated", "green"],
];

const FORMULAS = ["=VLOOKUP(price)", "=SUMIF(margin)", "=IF(scope>0)", "=PMT(rate)", "#REF!"];

const BADGES = [
  { label: "System secure", tone: "green", Icon: ShieldCheck, right: "27%", top: "14%", dur: "8s", pulse: true },
  { label: "Latency 12 ms", tone: "cyan", Icon: Gauge, right: "8%", top: "47%", dur: "9.5s" },
  { label: "100% parity", tone: "green", Icon: CheckCircle2, right: "19%", top: "64%", dur: "7.2s" },
];

const toneColor = (t) => (t === "cyan" ? "var(--cyan)" : t === "blue" ? "var(--blue-bright)" : "var(--green)");

export const HeroDecor = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const gridY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const clusterY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <div ref={ref} className="absolute inset-0 z-[11] pointer-events-none" aria-hidden="true" data-testid="hero-decor">
      {/* Parallaxing, drifting grid — masked to the right of the copy */}
      <motion.div style={{ y: gridY }} className="absolute inset-0 bg-grid bg-grid-animated hero-grid-mask" />

      {/* lg+ right-side ambient engineering cluster */}
      <motion.div style={{ y: clusterY }} className="hidden lg:block absolute inset-0">
        {/* faint dependency graph drifting on the far right */}
        <NodeGraph className="absolute right-[3%] top-[31%] w-[300px] opacity-25 animate-float-slow" />

        {/* floating grid fragments */}
        <div className="absolute right-[24%] top-[27%] h-20 w-20 rounded-xl grid-fragment border border-[var(--line)] rotate-6 opacity-50 animate-float" />
        <div className="absolute right-[8%] top-[59%] h-16 w-24 rounded-xl grid-fragment border border-[var(--line)] -rotate-6 opacity-40 animate-float-slow" />

        {/* drifting formula trails (cyan / blue, with one #REF! error in red) */}
        {FORMULAS.map((f, i) => (
          <span
            key={f}
            className={`formula-trail absolute font-mono text-[11px] ${
              f === "#REF!" ? "text-[var(--red)]" : i % 2 ? "text-[var(--cyan)]" : "text-[var(--blue-bright)]"
            }`}
            style={{
              right: `${10 + i * 7}%`,
              top: `${70 + (i % 3) * 6}%`,
              animationDuration: `${7 + i * 1.6}s`,
              animationDelay: `${i * 0.9}s`,
              opacity: 0.7,
            }}
          >
            {f}
          </span>
        ))}

        {/* live system-status cluster (cyan top accent) */}
        <div className="absolute right-[6%] top-[19%] w-[230px] glass rounded-2xl p-4 shadow-[0_40px_90px_-40px_rgba(0,0,0,0.9)] animate-float-tilt border-t border-[rgba(0,229,255,0.3)]">
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--muted-2)]">system://reverseai</span>
            <span className="h-2 w-2 rounded-full bg-[var(--green)] animate-pulse-soft" />
          </div>
          <div className="space-y-2">
            {STATUS_ROWS.map(([k, v, tone]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="font-mono text-[10px] text-[var(--muted-2)]">{k}</span>
                <span className="font-mono text-[10px]" style={{ color: toneColor(tone) }}>
                  {v}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* floating live-status glass badges */}
        {BADGES.map((b) => {
          const c = toneColor(b.tone);
          return (
            <div
              key={b.label}
              className={`absolute glass rounded-full pl-2 pr-3.5 py-1.5 flex items-center gap-2 animate-float ${b.pulse ? "validate-pulse" : ""}`}
              style={{ right: b.right, top: b.top, animationDuration: b.dur }}
            >
              <span className="grid place-items-center h-5 w-5 rounded-full" style={{ background: "rgba(255,255,255,0.06)", color: c }}>
                <b.Icon size={11} />
              </span>
              <span className="font-mono text-[10px] text-white/90 whitespace-nowrap">{b.label}</span>
              <span className="h-1.5 w-1.5 rounded-full animate-pulse-soft" style={{ background: c }} />
            </div>
          );
        })}
      </motion.div>
    </div>
  );
};
